import { Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import { FadeUp } from "@/components/site/Reveal";

const inr = (n) => `₹${n.toLocaleString("en-IN")}`;

const Strike = ({ children }) => (
    <span className="relative inline-block">
        <span className="absolute -left-1.5 -right-1.5 top-1/2 h-1 -translate-y-1/2 -rotate-2 rounded-full bg-[#f0356f] sm:h-1.5" />
        {children}
    </span>
);

const CardVisual = ({ src, alt, chip }) => (
    <div className="group/v relative overflow-hidden rounded-2xl border border-white/10">
        <img
            src={src}
            alt={alt}
            loading="lazy"
            decoding="async"
            className="h-44 w-full object-cover transition-transform duration-700 group-hover/v:scale-105 sm:h-48"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0b1422]/85 via-[#0b1422]/10 to-transparent" />
        <span className="absolute bottom-3 left-3 rounded-full border border-[#3fcbf3]/35 bg-[#070c15]/80 px-3 py-1 text-[9px] font-bold uppercase tracking-[0.18em] text-[#3fcbf3] backdrop-blur-sm">
            {chip}
        </span>
    </div>
);

const VALUE_FEATURES = [
    {
        num: "01",
        name: "Learning Hub",
        value: 9999,
        desc: "A growing library of fitness eBooks, videos and practical guides covering training, nutrition, fat loss, muscle building, habits and more.",
        src: "/brand/value-learning.webp",
        alt: "Premium fitness education library",
        chip: "eBooks · Videos · Guides",
    },
    {
        num: "02",
        name: "Recipe Library",
        value: 5999,
        desc: "Practical, delicious recipes that make following your nutrition plan easier — with clear nutritional information for every meal.",
        src: "/brand/value-recipes.webp",
        alt: "Premium healthy recipe collection",
        chip: "Macro-Matched Recipes",
    },
    {
        num: "03",
        name: "AI Coach",
        value: 4999,
        desc: "Contextual guidance for your workouts, nutrition, exercises, progress and SHREDxpro experience whenever you need it.",
        src: "/brand/value-ai.webp",
        alt: "AI fitness coach experience",
        chip: "24/7 Smart Guidance",
    },
];

const TOTAL = VALUE_FEATURES.reduce((a, f) => a + f.value, 0);
const ValueStack = () => (
        <section id="value" data-testid="value-section" className="relative overflow-hidden py-14 sm:py-20">
            <div className="pointer-events-none absolute -left-40 top-40 h-96 w-96 rounded-full bg-[#3fcbf3]/[0.07] blur-[130px]" />
            <div className="pointer-events-none absolute -right-40 top-[60%] h-96 w-96 rounded-full bg-[#f0356f]/[0.07] blur-[130px]" />

            <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
                <FadeUp>
                    <div className="mx-auto max-w-2xl text-center">
                        <p className="eyebrow">Membership Value</p>
                        <h2 className="font-display mt-4 text-4xl leading-[1.08] text-white sm:text-5xl lg:text-6xl">
                            More than just a fitness app.
                        </h2>
                        <p className="mt-6 text-base leading-relaxed text-[#9fb0c7]">
                            Get powerful tools, education and intelligent coaching features
                            designed to make your fitness journey easier, smarter and more
                            sustainable.
                        </p>
                    </div>
                </FadeUp>

                <div className="mt-14 flex flex-col items-center gap-8">
                    {VALUE_FEATURES.map((f, i) => (
                        <FadeUp key={f.num} delay={i * 0.1} className="w-full max-w-[400px]">
                            <article
                                data-testid={`value-card-${f.num}`}
                                className="card-surface group flex h-full flex-col p-7 transition-colors duration-300 hover:border-[#3fcbf3]/25 sm:p-8"
                            >
                                <CardVisual src={f.src} alt={f.alt} chip={f.chip} />
                                <div className="mt-2 flex items-start justify-between gap-4">
                                    <p className="pt-1 text-xs font-extrabold uppercase tracking-[0.22em] text-[#7d8da3]">
                                        {f.name}
                                    </p>
                                    <span className="font-display text-4xl leading-none text-white/10 transition-colors duration-300 group-hover:text-[#3fcbf3]/25">
                                        {f.num}
                                    </span>
                                </div>
                                <p className="font-display mt-2 text-5xl text-white">
                                    <Strike>{inr(f.value)}/-</Strike>
                                </p>
                                <span className="mt-4 inline-flex w-fit items-center gap-1.5 rounded-full border border-[#3fcbf3]/35 bg-[#3fcbf3]/10 px-4 py-1.5 text-[11px] font-extrabold uppercase tracking-[0.2em] text-[#3fcbf3]">
                                    <Check className="h-3.5 w-3.5" /> Included Free
                                </span>
                                <p className="mt-4 text-sm leading-relaxed text-[#9fb0c7]">{f.desc}</p>
                            </article>
                        </FadeUp>
                    ))}
                </div>

                <FadeUp delay={0.15}>
                    <div
                        data-testid="value-total"
                        className="mt-12 rounded-3xl bg-gradient-to-r from-[#45d1f5] to-[#f0356f] p-[1.5px] shadow-[0_0_60px_rgba(63,203,243,0.12)]"
                    >
                        <div className="flex flex-col items-center gap-6 rounded-3xl bg-[#0b1422] px-6 py-12 text-center sm:px-12 lg:flex-row lg:justify-between lg:text-left">
                            <div>
                                <p className="text-xs font-extrabold uppercase tracking-[0.3em] text-[#7d8da3]">
                                    Total value of included resources
                                </p>
                                <p className="font-display mt-2 text-5xl text-white sm:text-6xl" data-testid="value-total-amount">
                                    <Strike thick>{inr(TOTAL)}</Strike>
                                </p>
                                <p className="mt-3 max-w-md text-sm leading-relaxed text-[#9fb0c7]">
                                    Assigned standalone value of the resources and intelligent
                                    features above — never sold separately to members.
                                </p>
                            </div>
                            <div className="shrink-0">
                                <p className="bg-gradient-to-r from-[#45d1f5] to-[#f0356f] bg-clip-text font-display text-3xl uppercase leading-tight text-transparent sm:text-4xl">
                                    Included free<br />with SHREDxpro
                                </p>
                            </div>
                        </div>
                    </div>
                </FadeUp>

                <FadeUp delay={0.2}>
                    <div className="mt-16 text-center">
                        <h3 className="font-display text-3xl text-white sm:text-4xl" data-testid="value-cta-heading">
                            All of this. Inside one system.
                        </h3>
                        <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-[#9fb0c7]">
                            Training, education, nutrition resources, intelligent coaching and
                            automatic adaptation — all inside SHREDxpro.
                        </p>
                        <Link to="/register" data-testid="value-cta" className="btn-primary mt-8">
                            Start Your 7-Day Free Trial <ArrowRight className="h-4 w-4" />
                        </Link>
                    </div>
                </FadeUp>
            </div>
        </section>
);

export default ValueStack;
